/* eslint-disable import/no-unresolved */
import axios, { AxiosError } from 'axios';
import { getStorageValue } from 'src/hooks/useLocalStorage';
import { DateTime } from 'luxon';
import http from './http';

export type benefitChanges = {
  imageUrl: string;
  benefitId: string;
  benefitName: string;
  date: string;
  action: string;
  coverageAmount?: number;
  cost?: number;
  annual?: number;
  preTax?: number;
  postTax?: number;
  beneficiaries?: string;
  dependents?: string;
  approvalStatus?: string;
};

export type BenefitDetailChanges = {
  id: string;
  personnelNumber: string;
  employeeName: string;
  birthDate: string;
  ssn: string;
  email: string;
  description: string;
  submissionDate: string;
  provider: string;
  approvalStatus?: string;
  eventDate?: string;
  documents: [
    {
      name: string;
      url: string;
    },
  ];
  benefitsChanges: benefitChanges[];
};

export type EmployeeReviewType = {
  id: string;
  personalNumber?: string;
  createdBy?: string;
  employeeClass?: string;
  positionType?: string;
  employeeName: string;
  personnelNumber: string;
  submittedDate: string;
  summaryId?: string;
  email: string;
  change: string;
  createdAt?: string;
  requestEventType?: string;
  requestStatus?: string;
  approvalStatus?: string;
};

export type BenefitReviewChangesPost = {
  id: string;
  approvedList: string[];
  declinedList: string[];
  eventType: string;
  personnelNumber: string;
  approve: boolean;
  companyId: string;
};
export interface IApprovalParamsWithPagination {
  [x: string]: string | number | boolean | undefined;
  page: number;
  limit: number;
  companyId: string;
  startDate: string;
  endDate: string;
}

class ApprovalsService {
  static getPendingApprovals(
    params: IApprovalParamsWithPagination
  ): Promise<{ items: EmployeeReviewType[]; meta: { totalItems: number } }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }

    return http
      .get(`approvals/pendings?${stringParams}`, {
        headers: { companyId: params.companyId },
      })
      .then(resp => resp.data);
  }

  static getPendingApprovalsTotal(companyId: string): Promise<{ pendingChanges: number }> {
    return http
      .get('approvals/pendings/total', {
        headers: { companyId },
      })
      .then(resp => resp.data);
  }

  static getDetails(params: {
    eventType: string;
    personnelNumber: string;
    companyId: string;
    summaryId?: string;
  }): Promise<BenefitDetailChanges> {
    if (params.summaryId) {
      return http
        .get(
          `approvals/details?eventType=${params.eventType}&personnelNumber=${params.personnelNumber}&summaryId=${params.summaryId}`,
          {
            headers: { companyId: params.companyId },
          }
        )
        .then(resp => resp.data);
    }
    return http
      .get(`approvals/details?eventType=${params.eventType}&personnelNumber=${params.personnelNumber}`, {
        headers: { companyId: params.companyId },
      })
      .then(resp => resp.data);
  }

  static reviewChanges(data: BenefitReviewChangesPost) {
    const { companyId, ...body } = data;
    return http
      .post('/approvals', body, {
        headers: { companyId },
      })
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static reviewDependentChanges({ requestId, status }: { requestId: string; status: string }) {
    return http.post(`/approvals/contact?requestId=${requestId}&status=${status}`);
  }

  static downloadReviews(startDate: string, endDate: string): string {
    const startDateFormated = DateTime.fromISO(startDate).toFormat('yyyy-MM-dd');
    const endDateFormated = DateTime.fromISO(endDate).toFormat('yyyy-MM-dd');
    return `${process.env.REACT_APP_API_URL}approvals/export?token=${getStorageValue(
      'accessToken',
      null
    )}&startDate=${startDateFormated}&endDate=${endDateFormated}`;
  }

  static bulkApprove(data: { benefitSummaryIds: string[]; contactApprovalIds: string[]; companyId: string }) {
    const { companyId, ...body } = data;
    return http
      .post('/approvals/bulk', body, {
        headers: { companyId },
      })
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }
}

export default ApprovalsService;
